import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faSort, faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import { CoinContext } from "../../../context/CoinContext";
import LoadingTableSkeleton from "../../Elements/TableLoader";
import SynchronizedAreaChart2 from "../../Elements/Chart2";
import { useAccount } from "../../../context/AccountContext";

// SEGPOOL: segpool's own workers; SEGPROXY: miners connected through segproxy
type WorkerSource = 'SEGPOOL' | 'SEGPROXY';

interface WorkerData {
    workerId: string;
    source?: WorkerSource;
    hashRate15: string;
    hashRate24: string;
    rejected: string;
    status: string;
}

interface WorkerCounts {
    all: number;
    online: number;
    offline: number;
    dead: number;
}

interface WorkerChartResponse {
    data: any;

}
interface DataItem {
    time: string;
    hashrate: number;
}

interface ApiResponseItem {
    timeStamp: number;
    hash: number;
}

const Workers: React.FC = () => {
    const [allWorkersData, setAllWorkersData] = useState<WorkerData[]>([]);
    const [filter, setFilter] = useState('all');
    const [workersData, setWorkersData] = useState<WorkerData[]>([]);
    const [counts, setCounts] = useState<WorkerCounts>({ all: 0, online: 0, offline: 0, dead: 0 });
    const [total, setTotal] = useState({ hashRate15: 0, hashRate24: 0, rejected: 0 });
    const [openRowIndex, setOpenRowIndex] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(false)
    const [chartData, setChartData] = useState<DataItem[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" | null }>({
        key: "",
        direction: null,
    });

    const coinContext = useContext(CoinContext);
    const { activeAccount } = useAccount();


    const toggleRow = (index: number, worker: WorkerData) => {
        if (openRowIndex === index) {
            setOpenRowIndex(null);
        } else {
            setOpenRowIndex(index);
            handleWorkerChart(worker.workerId, worker.source);
        }
    };


    const cleanDigit = (n: any): string => {
        return n == null ? "0.00" : Number(n).toFixed(2);
    };

    const getWorkerData = (callback: (data: WorkerData[] | null) => void, coin: string) => {
        setLoading(true)
        axios.get(`/pool/get-workers-redis?hours=1&coin=${coin}`)
            .then(response => {
                callback(formatWorkerData(response.data?.data));
                setLoading(false)
            })
            .catch(e => {
                console.error(e);
                callback(null);
                setLoading(false)
            });
    };

    const handleWorkerChart = (workerId: string | number, source?: WorkerSource) => {
        if (coinContext?.selectedCoin) {
            const coin = coinContext?.selectedCoin;
            // a segpool and a proxy worker can share a name, so chart only this worker's source
            const sourceParam = source ? `&source=${source}` : '';
            axios.get(`/pool/worker/worker-chart-redis?coin=${coin}&worker=${workerId}${sourceParam}`)
                .then(response => {
                    const formattedData: DataItem[] = response.data.map((n: ApiResponseItem) => ({
                        time: formatTimeLabel(n.timeStamp),
                        hashrate: n.hash.toFixed(1),
                    }));

                    setChartData(formattedData);
                })
                .catch(error => {
                    console.error('Error fetching chart data:', error);
                    setChartData([]);
                });
        }
    };

    const formatTimeLabel = (timestamp: number): string => {
        return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatWorkerData = (data: any): WorkerData[] | null => {
        if (!data) return null;
        return data.map((o: any) => ({
            ...o,
            hashRate15: (o.hashRate15 / 1).toString(),
            hashRate24: (o.hashRate24 / 1).toString()
        }));
    };

    useEffect(() => {
        if (coinContext) {
            const coin = coinContext.selectedCoin;
            getWorkerData(data => {
                setAllWorkersData(data ?? []);
            }, coin);
        }
    }, [coinContext]);

    // Refresh worker data when active account changes
    useEffect(() => {
        if (activeAccount && coinContext) {
            const coin = coinContext.selectedCoin;
            getWorkerData(data => {
                setAllWorkersData(data ?? []);
            }, coin);
        }
    }, [activeAccount]);


    useEffect(() => {
        doFilter(filter);
        doCount();
    }, [allWorkersData, filter]);

    const doCount = () => {
        const newCount = { all: 0, online: 0, offline: 0, dead: 0 };
        allWorkersData.forEach(n => {
            switch (n.status.toLowerCase()) {
                case 'dead':
                    newCount.dead++;
                    break;
                case 'online':
                    newCount.online++;
                    newCount.all++;
                    break;
                case 'offline':
                    newCount.offline++;
                    newCount.all++;
                    break;
            }
        });
        setCounts(newCount);
    };

    const doFilter = (flag: string) => {
        setFilter(flag);
        const filteredWorkers = allWorkersData.filter(n =>
            flag === 'all' ? n.status.toLowerCase() !== 'dead' : n.status.toLowerCase() === flag
        );
        setWorkersData(filteredWorkers);
        const newTotal = { hashRate15: 0, hashRate24: 0, rejected: 0 };
        filteredWorkers.forEach(n => {
            newTotal.hashRate15 += Number(n.hashRate15);
            newTotal.hashRate24 += Number(n.hashRate24);
            newTotal.rejected += Number(n.rejected);
        });
        newTotal.rejected = filteredWorkers.length ? newTotal.rejected / filteredWorkers.length : 0;
        setTotal(newTotal);
    };

    const handleSort = (key: string) => {
        let direction: "asc" | "desc" = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });
    };

    const filteredAndSortedData = useMemo(() => {
        setOpenRowIndex(null)
        let filtered = workersData.filter((worker) =>
            worker?.workerId?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (sortConfig.key && sortConfig.direction) {
            filtered = [...filtered].sort((a, b) => {
                let aVal: any;
                let bVal: any;

                if (sortConfig.key === "workerId") {
                    aVal = a.workerId.toLowerCase();
                    bVal = b.workerId.toLowerCase();
                } else if (sortConfig.key === "hashRate15") {
                    aVal = parseFloat(a.hashRate15) || 0;
                    bVal = parseFloat(b.hashRate15) || 0;
                } else if (sortConfig.key === "hashRate24") {
                    aVal = parseFloat(a.hashRate24) || 0;
                    bVal = parseFloat(b.hashRate24) || 0;
                }

                if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
                return 0;
            });
        }

        return filtered;
    }, [workersData, searchTerm, sortConfig]);

    const renderSortIcon = (key: string) => {
        if (sortConfig.key !== key) return <FontAwesomeIcon icon={faSort} />;
        return sortConfig.direction === "asc" ? (
            <FontAwesomeIcon icon={faSortUp} />
        ) : (
            <FontAwesomeIcon icon={faSortDown} />
        );
    };

    return (
        <div className="flex flex-col p-5">
            <div className="md:flex md:justify-between md:items-center">
                {/* Filter Buttons - Now in same row on mobile */}
                <div className="flex space-x-2 p-2 overflow-x-auto pb-3">
                    {(['all', 'online', 'offline', 'dead'] as const).map(status => (
                        <button
                            key={status}
                            className={`flex-shrink-0 text-xs py-2.5 rounded-xl flex items-center justify-between px-4 transition-all duration-300 font-bold tracking-tight ${filter === status
                                ? 'bg-[#0E4D92] text-white shadow-md'
                                : 'bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-[#0E4D92] dark:hover:text-blue-300 hover:bg-blue-50/50 dark:hover:bg-slate-700/50'
                                }`}
                            disabled={filter === status}
                            onClick={() => doFilter(status)}
                        >
                            <div className="flex items-center">
                                {(status === 'online' || status === 'offline') && (
                                    <span
                                        className={`w-1.5 h-1.5 rounded-full mr-1.5 ${status === 'online' ? 'bg-green-500' : 'bg-red-500'}`}
                                    />
                                )}
                                {status === 'dead' && (
                                    <span className="w-1.5 h-1.5 rounded-full mr-1.5 bg-gray-500" />
                                )}
                                <span className="font-medium text-[10px] sm:text-xs">
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                </span>
                            </div>

                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold min-w-6 text-center ml-1.5 ${filter === status
                                ? 'bg-white/30 text-white'
                                : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300'
                                }`}>
                                {counts[status]}
                            </span>
                        </button>
                    ))}
                </div>

                {/* Search Bar - Below on mobile, to the right on desktop */}
                <div className="w-full md:w-auto p-2 mt-2 md:mt-0">
                    <input
                        type="text"
                        placeholder="Search by Worker..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="bg-white dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700 px-5 py-2.5 text-sm rounded-xl w-full md:w-64 focus:border-[#0E4D92] dark:focus:border-blue-500 outline-none transition-all text-slate-700 dark:text-slate-100 placeholder-slate-400 shadow-sm"
                    />
                </div>
            </div>

            {loading ?
                <LoadingTableSkeleton />
                :
                <>
                    <div className="hidden md:block overflow-hidden rounded-2xl border border-gray-100 dark:border-slate-800 shadow-sm transition-all">
                        <table className="w-full text-left text-slate-600 dark:text-slate-400">
                            <thead className="text-[#0E4D92] dark:text-blue-400 uppercase tracking-[0.1em] text-sm bg-slate-50/50 dark:bg-slate-800/60 backdrop-blur-sm border-b border-gray-100 dark:border-slate-800">
                                <tr>
                                    <th
                                        scope="col"
                                        className="px-4 py-3 font-bold cursor-pointer hover:text-[#0880D4] transition-colors"
                                        onClick={() => handleSort("workerId")}
                                    >
                                        <div className="flex items-center gap-1">
                                            Worker {renderSortIcon("workerId")}
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-4 py-3 font-bold text-center cursor-pointer hover:text-[#0880D4] transition-colors"
                                        onClick={() => handleSort("hashRate15")}
                                    >
                                        <div className="flex items-center justify-center gap-1">
                                            15m Hashrate {renderSortIcon("hashRate15")}
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-4 py-3 font-bold text-center cursor-pointer hover:text-[#0880D4] transition-colors"
                                        onClick={() => handleSort("hashRate24")}
                                    >
                                        <div className="flex items-center justify-center gap-1">
                                            24hr Hashrate {renderSortIcon("hashRate24")}
                                        </div>
                                    </th>
                                    <th scope="col" className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAndSortedData.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="text-center font-medium text-slate-400 py-6 text-sm">
                                            No active workers found
                                        </td>
                                    </tr>
                                ) : (
                                    filteredAndSortedData.map((worker, index) => (
                                        <React.Fragment key={index}>
                                            <tr
                                                className="bg-white dark:bg-slate-800/50 border-b border-blue-50/50 dark:border-slate-700/30 hover:bg-blue-50/20 dark:hover:bg-slate-700/50 transition-all cursor-pointer group"
                                                onClick={() => toggleRow(index, worker)}
                                            >
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center font-medium text-[#03436f] dark:text-white">
                                                        <div className="relative mr-2">
                                                            <div className={`w-2 h-2 rounded-full ${worker?.status === "ONLINE" ? "bg-emerald-500" : "bg-rose-500"} relative z-10`} />
                                                            {worker?.status === "ONLINE" && <div className="absolute inset-0 w-2 h-2 rounded-full bg-emerald-500 animate-ping opacity-60" />}
                                                        </div>
                                                        <span className="text-sm">{worker?.workerId}</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <div>
                                                        <span className="text-sm font-medium text-[#03436f] dark:text-blue-400">{cleanDigit(worker?.hashRate15)}</span>
                                                        <span className="text-[8px] ml-1 font-medium opacity-40">TH/S</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <div>
                                                        <span className="text-sm font-medium text-[#03436f] dark:text-blue-400">{cleanDigit(worker?.hashRate24)}</span>
                                                        <span className="text-[8px] ml-1 font-medium opacity-40">TH/S</span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <div className={`inline-flex items-center justify-center w-7 h-7 rounded-lg bg-slate-50 dark:bg-slate-700 group-hover:bg-[#0880D4] group-hover:text-white transition-all duration-300 ${openRowIndex === index ? "rotate-180 bg-[#0880D4] text-white" : "text-slate-400"}`}>
                                                        <FontAwesomeIcon icon={faChevronDown} className="text-[10px]" />
                                                    </div>
                                                </td>
                                            </tr>
                                            {openRowIndex === index && (
                                                <tr className="bg-blue-50/20 dark:bg-slate-900/40">
                                                    <td colSpan={4} className="p-0 border-b border-blue-100 dark:border-slate-700">
                                                        <div className="p-4 animate-in fade-in slide-in-from-top-4 duration-300">
                                                            <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl p-4 rounded-xl border border-white dark:border-slate-700 shadow-md">
                                                                <div className="flex justify-between items-center mb-4">
                                                                    <div>
                                                                        <h3 className="text-lg font-bold text-[#03436f] dark:text-white">
                                                                            Worker Details
                                                                        </h3>
                                                                        <p className="text-xs font-semibold text-[#0880D4] uppercase tracking-widest mt-1">24hr Hashrate Analysis</p>
                                                                    </div>
                                                                </div>
                                                                <SynchronizedAreaChart2 data={chartData} />
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* Mobile Card Layout */}
                    <div className="md:hidden space-y-3">
                        {filteredAndSortedData.map((worker, index) => (
                            <div
                                key={index}
                                className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-md rounded-xl p-4 border border-blue-50 dark:border-slate-700/50 shadow-lg transition-all active:scale-95"
                                onClick={() => toggleRow(index, worker)}
                            >
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                        <div className="relative">
                                            <div className={`w-2.5 h-2.5 rounded-full ${worker?.status === "ONLINE" ? "bg-emerald-500" : "bg-rose-500"} relative z-10`} />
                                            {worker?.status === "ONLINE" && <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping opacity-60" />}
                                        </div>
                                        <span className="font-bold text-[#03436F] dark:text-white text-base">{worker?.workerId}</span>
                                    </div>
                                    <div className={`w-7 h-7 rounded-lg bg-slate-50 dark:bg-slate-700 flex items-center justify-center transition-all ${openRowIndex === index ? "rotate-180 bg-[#0880D4] text-white" : "text-slate-400"}`}>
                                        <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-50 dark:border-slate-700/50">
                                    <div>
                                        <p className="text-[9px] font-semibold text-blue-400 dark:text-slate-500 uppercase tracking-widest mb-1">15m Hash</p>
                                        <p className="text-lg font-bold text-[#03436f] dark:text-white">{cleanDigit(worker?.hashRate15)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-semibold text-blue-400 dark:text-slate-500 uppercase tracking-widest mb-1">24h Hash</p>
                                        <p className="text-lg font-bold text-[#03436f] dark:text-white">{cleanDigit(worker?.hashRate24)}</p>
                                    </div>
                                </div>
                                {openRowIndex === index && (
                                    <div className="mt-6 pt-4 border-t border-slate-50 dark:border-slate-700/50 animate-in fade-in zoom-in-95 duration-300">
                                        <div className="bg-slate-50/50 dark:bg-slate-900/50 rounded-xl p-3">
                                            <SynchronizedAreaChart2 data={chartData} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                </>
            }
        </div>
    );
};

export default Workers;
